/**
 * Behavioral biometrics collector (P5) — always-on, timing only.
 *
 * PRIVACY CONTRACT: this module NEVER records input *values*. It only measures
 * timing and motion dynamics: when keys go down/up (not which keys), how the
 * pointer moves (not where it clicks), scroll rhythm, paste usage, tab focus
 * changes, and total session age. No DOM content, no field values, no key
 * identities ever enter a buffer.
 *
 * Capture is "always-on": `startBehaviorCapture()` is invoked from `gs.config`
 * so dynamics accumulate across the whole page lifetime. `snapshotBehavior()`
 * reads the current aggregates at token-seal time without stopping capture, so
 * a single page can mint multiple tokens (login, then checkout) each carrying
 * the dynamics observed up to that moment.
 */

import type { BehaviorSignals } from "../types.ts";

const RING = 256; // bounded buffers — fixed memory, no unbounded growth

interface Ring {
  buf: Float64Array;
  len: number;
  idx: number;
}

function makeRing(): Ring {
  return { buf: new Float64Array(RING), len: 0, idx: 0 };
}

function push(r: Ring, v: number): void {
  r.buf[r.idx] = v;
  r.idx = (r.idx + 1) % RING;
  if (r.len < RING) r.len++;
}

function values(r: Ring): number[] {
  const out: number[] = [];
  for (let i = 0; i < r.len; i++) out.push(r.buf[i]);
  return out;
}

function avg(r: Ring): number | null {
  if (r.len === 0) return null;
  let s = 0;
  for (let i = 0; i < r.len; i++) s += r.buf[i];
  return s / r.len;
}

interface CaptureState {
  started: boolean;
  startedAt: number;

  // pointer
  lastPoint: { x: number; y: number; t: number } | null;
  angles: Ring; // turn angles between successive segments (entropy source)
  moveCount: number;

  // keystroke
  keyDownAt: Map<number, number>; // keyCode -> downTime (timing only; never logged)
  dwell: Ring; // keyup - keydown
  flight: Ring; // keydown(n) - keyup(n-1)
  lastKeyUpAt: number | null;

  // form fill — chars typed vs elapsed within editable fields
  formCharCount: number;
  formFirstInputAt: number | null;
  formLastInputAt: number | null;

  // scroll
  lastScrollAt: number | null;
  scrollGaps: Ring;

  // misc
  copyPasteUsed: boolean;
  tabSwitches: number;
}

let s: CaptureState | null = null;
const cleanups: Array<() => void> = [];

function nowMs(): number {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function on<K extends keyof DocumentEventMap>(
  target: Document | Window,
  type: K,
  handler: (ev: DocumentEventMap[K]) => void,
  opts?: AddEventListenerOptions,
): void {
  try {
    (target as Document).addEventListener(
      type,
      handler as EventListener,
      { passive: true, capture: true, ...opts },
    );
    cleanups.push(() =>
      (target as Document).removeEventListener(type, handler as EventListener, { capture: true }),
    );
  } catch {
    /* environment without DOM */
  }
}

function isEditable(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node || !node.tagName) return false;
  const tag = node.tagName.toUpperCase();
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    const type = (node.getAttribute("type") || "text").toLowerCase();
    // Exclude non-text inputs; never inspect the value itself.
    return !["checkbox", "radio", "button", "submit", "range", "file", "color"].includes(type);
  }
  return node.isContentEditable === true;
}

/** Begin always-on capture. Idempotent — safe to call from every `gs.config`. */
export function startBehaviorCapture(): void {
  if (s?.started) return;
  if (typeof document === "undefined" || typeof window === "undefined") return;

  s = {
    started: true,
    startedAt: nowMs(),
    lastPoint: null,
    angles: makeRing(),
    moveCount: 0,
    keyDownAt: new Map(),
    dwell: makeRing(),
    flight: makeRing(),
    lastKeyUpAt: null,
    formCharCount: 0,
    formFirstInputAt: null,
    formLastInputAt: null,
    lastScrollAt: null,
    scrollGaps: makeRing(),
    copyPasteUsed: false,
    tabSwitches: 0,
  };
  const st = s;

  // ---- pointer dynamics: record turn angles between movement segments ----
  on(document, "pointermove", (ev) => {
    const e = ev as PointerEvent;
    const t = nowMs();
    const p = { x: e.clientX, y: e.clientY, t };
    st.moveCount++;
    const prev = st.lastPoint;
    if (prev) {
      const dx = p.x - prev.x;
      const dy = p.y - prev.y;
      if (dx !== 0 || dy !== 0) {
        const angle = Math.atan2(dy, dx); // direction only — no coordinates stored
        push(st.angles, angle);
      }
    }
    st.lastPoint = p;
  });

  // ---- keystroke dynamics: dwell + flight, never key identity content ----
  on(document, "keydown", (ev) => {
    const e = ev as KeyboardEvent;
    if (e.repeat) return;
    const t = nowMs();
    // keyCode is used only as an ephemeral map key to pair with keyup timing.
    if (!st.keyDownAt.has(e.keyCode)) st.keyDownAt.set(e.keyCode, t);
    if (st.lastKeyUpAt != null) {
      const flight = t - st.lastKeyUpAt;
      if (flight >= 0 && flight < 5000) push(st.flight, flight);
    }
  });
  on(document, "keyup", (ev) => {
    const e = ev as KeyboardEvent;
    const t = nowMs();
    const down = st.keyDownAt.get(e.keyCode);
    if (down != null) {
      const dwell = t - down;
      if (dwell >= 0 && dwell < 5000) push(st.dwell, dwell);
      st.keyDownAt.delete(e.keyCode);
    }
    st.lastKeyUpAt = t;
  });

  // ---- form-fill speed: count chars typed into editable fields over time ----
  on(document, "input", (ev) => {
    if (!isEditable(ev.target)) return;
    const t = nowMs();
    if (st.formFirstInputAt == null) st.formFirstInputAt = t;
    st.formLastInputAt = t;
    // Approximate keystroke-driven characters without reading the value.
    const ie = ev as InputEvent;
    if (ie.inputType === "insertText" || ie.inputType === "insertCompositionText") {
      st.formCharCount += ie.data ? ie.data.length : 1;
    } else if (ie.inputType === "insertFromPaste") {
      st.copyPasteUsed = true;
    }
  });

  // ---- scroll rhythm ----
  on(window, "scroll", () => {
    const t = nowMs();
    if (st.lastScrollAt != null) {
      const gap = t - st.lastScrollAt;
      if (gap >= 0 && gap < 60_000) push(st.scrollGaps, gap);
    }
    st.lastScrollAt = t;
  });

  // ---- paste + tab visibility ----
  on(document, "paste", () => {
    st.copyPasteUsed = true;
  });
  on(document, "visibilitychange", () => {
    if (document.visibilityState === "hidden") st.tabSwitches++;
  });
}

/** Stop capture and detach all listeners (used by GDPR/teardown paths). */
export function stopBehaviorCapture(): void {
  while (cleanups.length) {
    try {
      cleanups.pop()!();
    } catch {
      /* ignore */
    }
  }
  s = null;
}

/** Shannon entropy (normalized 0..1) of pointer turn angles, binned into 16. */
function angleEntropy(r: Ring): number | null {
  if (r.len < 8) return null;
  const bins = new Array(16).fill(0);
  const vals = values(r);
  for (const a of vals) {
    // map [-PI, PI] -> [0, 16)
    let bin = Math.floor(((a + Math.PI) / (2 * Math.PI)) * 16);
    if (bin < 0) bin = 0;
    if (bin > 15) bin = 15;
    bins[bin]++;
  }
  const n = vals.length;
  let h = 0;
  for (const c of bins) {
    if (c === 0) continue;
    const p = c / n;
    h -= p * Math.log2(p);
  }
  return Math.min(1, h / 4); // max entropy for 16 bins = log2(16) = 4
}

/**
 * Bot-likelihood heuristic in [0..1]. Higher = more automation-like:
 * near-zero pointer entropy, robotically uniform keystroke timing, and
 * machine-gun form-fill speed all push the score up. Returns null when there
 * is too little interaction to judge.
 */
function riskScore(
  entropy: number | null,
  dwellAvg: number | null,
  flightAvg: number | null,
  formCps: number | null,
  moveCount: number,
): number | null {
  const factors: number[] = [];

  if (moveCount >= 10 && entropy != null) {
    // Straight-line / no-jitter movement is suspicious.
    factors.push(entropy < 0.2 ? 0.9 : entropy < 0.4 ? 0.5 : 0.1);
  }
  if (dwellAvg != null) {
    // Sub-12ms or supernaturally uniform dwell -> automation.
    factors.push(dwellAvg < 12 ? 0.85 : dwellAvg < 25 ? 0.4 : 0.1);
  }
  if (flightAvg != null) {
    factors.push(flightAvg < 8 ? 0.85 : flightAvg < 20 ? 0.4 : 0.1);
  }
  if (formCps != null) {
    // >25 chars/sec sustained is faster than human typing.
    factors.push(formCps > 25 ? 0.9 : formCps > 15 ? 0.5 : 0.1);
  }

  if (factors.length === 0) return null;
  const score = factors.reduce((a, b) => a + b, 0) / factors.length;
  return Math.round(score * 100) / 100;
}

function empty(): BehaviorSignals {
  return {
    mouse_path_entropy: null, keystroke_dwell_avg_ms: null, keystroke_flight_avg_ms: null,
    scroll_cadence_ms: null, form_fill_speed_cps: null, copy_paste_used: null,
    tab_switches: null, session_duration_ms: null, risk_score: null,
  };
}

/** Read a snapshot of accumulated dynamics. Capture continues running. */
export function snapshotBehavior(): BehaviorSignals {
  if (!s) return empty();

  const entropy = angleEntropy(s.angles);
  const dwellAvg = avg(s.dwell);
  const flightAvg = avg(s.flight);
  const scrollCadence = avg(s.scrollGaps);

  let formCps: number | null = null;
  if (
    s.formCharCount > 0 &&
    s.formFirstInputAt != null &&
    s.formLastInputAt != null &&
    s.formLastInputAt > s.formFirstInputAt
  ) {
    const seconds = (s.formLastInputAt - s.formFirstInputAt) / 1000;
    if (seconds > 0) formCps = Math.round((s.formCharCount / seconds) * 100) / 100;
  }

  return {
    mouse_path_entropy: entropy,
    keystroke_dwell_avg_ms: dwellAvg != null ? Math.round(dwellAvg) : null,
    keystroke_flight_avg_ms: flightAvg != null ? Math.round(flightAvg) : null,
    scroll_cadence_ms: scrollCadence != null ? Math.round(scrollCadence) : null,
    form_fill_speed_cps: formCps,
    copy_paste_used: s.copyPasteUsed,
    tab_switches: s.tabSwitches,
    session_duration_ms: Math.round(nowMs() - s.startedAt),
    risk_score: riskScore(entropy, dwellAvg, flightAvg, formCps, s.moveCount),
  };
}
